import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { ReportStatus, ReportType } from '@repo/shared/types';
import { parseAsStringEnum, useQueryStates } from 'nuqs';
import { Activity, useMemo } from 'react';
import { type LoaderFunctionArgs, Outlet, redirect, useLoaderData, useParams } from 'react-router';
import { toast } from 'sonner';
import { ReportTypeSidebarCard } from '@/components/report/report-type-sidebar-card';
import { StepAccordion } from '@/components/report/step-accordion';
import { ReportWizardShell } from '@/components/report/wizard-shell';
import { useONavigate } from '@/hooks/use-organization-navigate';
import {
  type FormSteps,
  FormStepsKeys,
  getStepMeta,
  getStepsForReportType,
  getWizardStepLocks,
} from '@/lib/report';
import { trpc } from '@/providers/trpc';
import { trpc as trpcServer } from '@/providers/trpc-client';
import { useSetUnsavedChanges, useUnsavedChanges } from '@/providers/unsaved-changes';

// ── Loader ────────────────────────────────────────────────────────────────────

const loader = async ({ params }: LoaderFunctionArgs) => {
  const { organizationId, reportId } = params;
  if (!organizationId || !reportId) return redirect('/');

  if (reportId === 'new') {
    return {
      report: {
        _id: 'new',
        reportType: ReportType.KYC,
      },
    };
  }
  const report = await trpcServer.report.getReport.query(
    { reportId },
    {
      context: {
        organizationId: organizationId,
      },
    },
  );

  if (!report) {
    return redirect(`/o/${organizationId}/reports`);
  }

  // Only DRAFT reports can be edited in the wizard
  if (report.status !== ReportStatus.DRAFT) {
    return redirect(`/o/${organizationId}/reports/${reportId}`);
  }

  return { report };
};

export { loader as reportCreateWizardLoader };

// ── Layout component ──────────────────────────────────────────────────────────

export function ReportCreateWizardLayout() {
  useLocale();

  const { report: initialReport } = useLoaderData<typeof loader>();
  const { organizationId = '', reportId = '' } = useParams();
  const isNewReport = reportId === 'new';
  const oNavigate = useONavigate();

  const [queryStates, setQueryStates] = useQueryStates(
    {
      step: parseAsStringEnum(FormStepsKeys).withDefault('reportType'),
      reportType: parseAsStringEnum([ReportType.KYC, ReportType.KYB]).withDefault(
        initialReport.reportType,
      ),
    },
    {
      urlKeys: {
        // And remap them to shorter keys in the URL
        step: 'step',
        reportType: 'srt',
      },
    },
  );

  const steps = useMemo(
    () =>
      getStepsForReportType(
        isNewReport && queryStates.reportType ? queryStates.reportType : initialReport.reportType,
        isNewReport,
      ),
    [getLocale(), isNewReport, queryStates.reportType, initialReport.reportType],
  );

  const { data: reportData } = trpc.report.getReport.useQuery(
    { reportId },
    { initialData: initialReport as never, enabled: !isNewReport },
  );
  const report = reportData ?? initialReport;

  const currentStepMeta = getStepMeta(report.reportType, queryStates.step) ?? steps[0]!;
  const lockedSteps = getWizardStepLocks(report, report.reportType, isNewReport);

  const { currentPageHasUnsavedChanges, showUnsavedChangesModal } = useUnsavedChanges();
  const { clearUnsavedChanges } = useSetUnsavedChanges();

  const navigateToStep = (next: FormSteps) => {
    if (lockedSteps[next as FormSteps]) return;
    const target = `/o/${organizationId}/reports/create/${reportId}?step=${encodeURIComponent(next)}`;
    if (currentPageHasUnsavedChanges) {
      showUnsavedChangesModal(target);
    } else {
      void setQueryStates({ step: next });
    }
  };

  const onChangeReportTypeConfirm = () => {
    void clearUnsavedChanges();
    toast.success(t('auth.reportDiscarded'));
    void setQueryStates({ step: 'reportType', reportType: ReportType.KYC });
    void oNavigate({
      to: '/reports/create',
      options: { replace: true, flushSync: true },
    });
  };

  return (
    <ReportWizardShell
      description={currentStepMeta.description}
      formStepIndex={currentStepMeta.index}
      formStepTotal={isNewReport ? steps.length : steps.length + 1}
      sidebar={
        <>
          <Activity mode={isNewReport ? 'hidden' : 'visible'}>
            <ReportTypeSidebarCard
              onChangeReportTypeConfirm={onChangeReportTypeConfirm}
              reportType={report.reportType}
            />
          </Activity>
          <StepAccordion
            currentStep={queryStates.step}
            lockedSteps={lockedSteps}
            onNavigate={navigateToStep}
            stepNumberOffset={isNewReport ? 0 : 1}
            steps={steps}
          />
        </>
      }
      title={currentStepMeta.title}
      topLeftLabel={t('common.reportType', { type: report.reportType })}
    >
      <Outlet />
    </ReportWizardShell>
  );
}
